import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import type { Task, Trip } from "@/api.types.v2";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { shipmentReference } from "@/lib/shipment-reference";
import {
  taskInstructionLabel,
  taskInstructions,
} from "@/features/dispatch/selectors";
import { taskRefs, taskAddressLine, taskRoutingPin } from "./task-labels";

export interface BuildingVisitDraft {
  id: number;
  taskIds: string[];
  waypointId: string;
  anchorTaskId: string;
  confirmed: boolean;
  confirmedReview?: BuildingVisitReview;
}

interface BuildingVisitReview {
  reviewedTaskIds: string[];
  fingerprint: string;
}

function captureBuildingVisitReview(
  visit: BuildingVisitDraft,
  tasks: Task[],
  targetTrip?: Trip
): BuildingVisitReview | null {
  const waypoint = visit.waypointId
    ? targetTrip?.waypoints?.find((item) => item.id === visit.waypointId)
    : undefined;
  if (visit.waypointId && !waypoint?.tasks) return null;
  const selected = visit.taskIds.map((id) =>
    tasks.find((task) => task.id === id)
  );
  if (selected.some((task) => !task)) return null;
  const anchorTask = tasks.find((task) => task.id === visit.anchorTaskId);
  const routingPin =
    waypoint?.locationKey ?? (anchorTask && taskRoutingPin(anchorTask));
  if (!routingPin) return null;
  const existing = waypoint?.tasks ?? [];
  return {
    reviewedTaskIds: [...existing.map((task) => task.id), ...visit.taskIds],
    fingerprint: JSON.stringify({
      waypointId: visit.waypointId,
      anchorTaskId: visit.waypointId ? null : visit.anchorTaskId,
      routingPin,
      selected: selected
        .map((task) => [task?.id, task && taskRoutingPin(task)])
        .sort(),
      existing: existing.map((task) => [task.id, taskRoutingPin(task)]).sort(),
    }),
  };
}

export function verifiedBuildingVisitsForAssignment(
  visits: BuildingVisitDraft[],
  selectedTaskIds: string[],
  target: string,
  targetTrip: Trip | undefined,
  tasks: Task[]
) {
  const selectedIds = new Set(selectedTaskIds);
  const usedIds = new Set<string>();
  const usedWaypointIds = new Set<string>();
  for (const visit of visits) {
    if (
      !visit.confirmed ||
      !visit.confirmedReview ||
      visit.confirmedReview.fingerprint !==
        captureBuildingVisitReview(visit, tasks, targetTrip)?.fingerprint ||
      visit.taskIds.length < (visit.waypointId ? 1 : 2) ||
      (!visit.waypointId && !visit.taskIds.includes(visit.anchorTaskId)) ||
      (visit.waypointId && target === "new")
    )
      return null;
    if (visit.waypointId) {
      if (usedWaypointIds.has(visit.waypointId)) return null;
      if (
        !targetTrip?.waypoints?.some(
          (waypoint) => waypoint.id === visit.waypointId
        )
      )
        return null;
      usedWaypointIds.add(visit.waypointId);
    }
    for (const id of visit.taskIds) {
      if (!selectedIds.has(id) || usedIds.has(id)) return null;
      usedIds.add(id);
    }
  }
  return visits.map((visit) => ({
    taskIds: visit.taskIds,
    reviewedTaskIds: visit.confirmedReview?.reviewedTaskIds ?? [],
    ...(visit.waypointId ? { waypointId: visit.waypointId } : {}),
    ...(!visit.waypointId ? { anchorTaskId: visit.anchorTaskId } : {}),
    confirmed: true as const,
  }));
}

export function BuildingVisitPlanner({
  tasks,
  selectedTaskIds: sel,
  target,
  targetTrip,
  visits: buildingVisits,
  onChange: setBuildingVisits,
}: {
  tasks: Task[];
  selectedTaskIds: string[];
  target: string;
  targetTrip: Trip | undefined;
  visits: BuildingVisitDraft[];
  onChange: Dispatch<SetStateAction<BuildingVisitDraft[]>>;
}) {
  const [nextVisitId, setNextVisitId] = useState(1);
  useEffect(() => {
    setBuildingVisits((current) => {
      let changed = false;
      const next = current.map((visit) => {
        if (
          !visit.confirmed ||
          visit.confirmedReview?.fingerprint ===
            captureBuildingVisitReview(visit, tasks, targetTrip)?.fingerprint
        )
          return visit;
        changed = true;
        return { ...visit, confirmed: false, confirmedReview: undefined };
      });
      return changed ? next : current;
    });
  }, [tasks, targetTrip, setBuildingVisits]);
  const updateVisit = (
    id: number,
    update: (visit: BuildingVisitDraft) => BuildingVisitDraft
  ) =>
    setBuildingVisits((current) =>
      current.map((visit) => (visit.id === id ? update(visit) : visit))
    );
  return (
    <>
      {sel.length > 0 && (
        <section
          className="space-y-2 border-t pt-3"
          aria-label="Building visits"
        >
          <div className="flex items-center justify-between gap-2 px-1">
            <div>
              <h3 className="text-sm font-semibold">
                Combined building visits
              </h3>
              <p className="text-xs text-zinc-500">
                By default, Tasks at the exact same pin combine automatically.
                Confirm a building visit here when different pins use the same
                building and compatible access.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setBuildingVisits((current) => [
                  ...current,
                  {
                    id: nextVisitId,
                    taskIds: [],
                    waypointId: "",
                    anchorTaskId: "",
                    confirmed: false,
                  },
                ]);
                setNextVisitId((id) => id + 1);
              }}
            >
              Add visit
            </Button>
          </div>
          {buildingVisits.map((visit, index) => (
            <fieldset
              key={visit.id}
              className="space-y-2 rounded-lg border p-3"
            >
              <legend className="text-xs font-semibold">
                Building visit {index + 1}
              </legend>
              {tasks
                .filter((task) => sel.includes(task.id))
                .map((task) => (
                  <label
                    key={task.id}
                    className="flex items-center gap-2 text-xs"
                  >
                    <input
                      type="checkbox"
                      aria-label={`Add TSK-${task.taskNumber} to building visit ${index + 1}`}
                      checked={visit.taskIds.includes(task.id)}
                      disabled={buildingVisits.some(
                        (other) =>
                          other.id !== visit.id &&
                          other.taskIds.includes(task.id)
                      )}
                      onChange={(event) =>
                        updateVisit(visit.id, (current) => ({
                          ...current,
                          confirmed: false,
                          taskIds: event.target.checked
                            ? [...current.taskIds, task.id]
                            : current.taskIds.filter((id) => id !== task.id),
                          anchorTaskId: event.target.checked
                            ? current.anchorTaskId || task.id
                            : current.anchorTaskId === task.id
                              ? (current.taskIds.find((id) => id !== task.id) ??
                                "")
                              : current.anchorTaskId,
                        }))
                      }
                    />
                    {shipmentReference(taskRefs(task).shipmentNumber)} ·{" "}
                    {taskAddressLine(task)}
                    {(task.addressLine2 ?? task.address?.line2) &&
                      ` · Unit ${task.addressLine2 ?? task.address?.line2}`}
                    {task.contactName && ` · ${task.contactName}`}
                    {taskInstructions(task) &&
                      ` · ${taskInstructionLabel(task)}: ${taskInstructions(task)}`}
                    {taskRoutingPin(task) &&
                      ` · Pin ${taskRoutingPin(task)?.replace(",", ", ")}`}
                  </label>
                ))}
              {!visit.waypointId && visit.taskIds.length > 0 && (
                <div className="space-y-1 text-xs">
                  <span>Routing pin for this visit</span>
                  <Select
                    value={visit.anchorTaskId}
                    onValueChange={(value) =>
                      updateVisit(visit.id, (current) => ({
                        ...current,
                        anchorTaskId: value,
                        confirmed: false,
                      }))
                    }
                  >
                    <SelectTrigger
                      aria-label="Routing pin for this visit"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {visit.taskIds.map((id) => {
                        const task = tasks.find((item) => item.id === id);
                        return (
                          <SelectItem key={id} value={id}>
                            TSK-{task?.taskNumber} ·{" "}
                            {task ? taskAddressLine(task) : "Unknown address"}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {target !== "new" && (
                <div className="space-y-1 text-xs">
                  <span>Add to existing stop, if this is the same visit</span>
                  <Select
                    value={visit.waypointId || "new"}
                    onValueChange={(value) =>
                      updateVisit(visit.id, (current) => ({
                        ...current,
                        waypointId: value === "new" ? "" : value,
                        confirmed: false,
                      }))
                    }
                  >
                    <SelectTrigger
                      aria-label="Add to existing stop, if this is the same visit"
                      className="w-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Create a new stop</SelectItem>
                      {targetTrip?.waypoints
                        ?.filter(
                          (waypoint) =>
                            (waypoint.tasks?.length ?? 0) > 0 &&
                            waypoint.tasks?.some(
                              (task) =>
                                !["COMPLETED", "FAILED", "CANCELLED"].includes(
                                  task.status
                                )
                            )
                        )
                        .map((waypoint) => (
                          <SelectItem key={waypoint.id} value={waypoint.id}>
                            Stop {waypoint.sequence} ·{" "}
                            {waypoint.tasks?.[0]?.address?.line1 ??
                              waypoint.tasks?.[0]?.addressLine1 ??
                              "Unknown address"}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {visit.waypointId &&
                targetTrip?.waypoints
                  ?.find((waypoint) => waypoint.id === visit.waypointId)
                  ?.tasks?.map((task) => (
                    <div key={task.id} className="text-xs text-zinc-600">
                      TSK-{task.taskNumber} · {task.contactName ?? "No contact"}
                      {` · ${shipmentReference(taskRefs(task).shipmentNumber)} · ${taskAddressLine(task)}`}
                      {(task.addressLine2 ?? task.address?.line2) &&
                        ` · Unit ${task.addressLine2 ?? task.address?.line2}`}
                      {taskInstructions(task) &&
                        ` · ${taskInstructionLabel(task)}: ${taskInstructions(task)}`}
                      {taskRoutingPin(task) &&
                        ` · Pin ${taskRoutingPin(task)?.replace(",", ", ")}`}
                    </div>
                  ))}
              <label className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  aria-label={`Confirm building visit ${index + 1}`}
                  checked={
                    visit.confirmed &&
                    visit.confirmedReview?.fingerprint ===
                      captureBuildingVisitReview(visit, tasks, targetTrip)
                        ?.fingerprint
                  }
                  onChange={(event) =>
                    updateVisit(visit.id, (current) => {
                      const confirmedReview = event.target.checked
                        ? captureBuildingVisitReview(current, tasks, targetTrip)
                        : null;
                      return {
                        ...current,
                        confirmed: Boolean(confirmedReview),
                        confirmedReview: confirmedReview ?? undefined,
                      };
                    })
                  }
                />
                I verified the same building and compatible entrance/access for
                this visit.
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  setBuildingVisits((current) =>
                    current.filter((item) => item.id !== visit.id)
                  )
                }
              >
                Remove visit
              </Button>
            </fieldset>
          ))}
        </section>
      )}
    </>
  );
}
